/**
 * The pane-side focus target for Code Mode's leader.
 *
 * `Office.addin.showAsTaskpane()` makes the pane visible but does not move
 * keyboard focus into it, and Office exposes no API that does. The only lever
 * available from inside the WebView is focusing a real DOM element and hoping
 * the host honours it — so this is a genuine `<input>`, not a `tabIndex={-1}`
 * container: a text input is the strongest focus target a page has, and an
 * invisible div was measurably not enough (letters kept landing on the slide).
 *
 * Two consequences worth knowing:
 *
 * - It must be *visually* hidden but still focusable. `display: none` and
 *   `visibility: hidden` elements cannot take focus at all, hence the 1x1
 *   transparent treatment in TaskPane rather than either of those.
 * - Once focused it is an editable target, which the keydown guard would
 *   normally skip to avoid eating the user's typing (BR-7). `isFocusCatcher`
 *   below is the single exception to that rule — this input exists only to
 *   hold focus, never to receive text, and the accumulator preventDefaults
 *   every keystroke it consumes so nothing is ever inserted into it.
 */

export const CODE_MODE_FOCUS_CATCHER_ID = "codeModeFocusCatcher";

/** Delays for successive focus attempts. `showAsTaskpane()` resolving means
 * "shown", not "painted and focusable", so a single immediate attempt loses
 * the race on a pane that was closed. Escalating retries cost nothing once
 * one succeeds — each checks first and bails. */
const RETRY_DELAYS_MS = [0, 60, 180, 400];

/** True when `target` is the focus catcher — the one editable element the
 * accumulator is allowed to read keystrokes from. */
export function isFocusCatcher(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && target.id === CODE_MODE_FOCUS_CATCHER_ID;
}

function attempt(): boolean {
  const el = document.getElementById(CODE_MODE_FOCUS_CATCHER_ID);
  if (!el) return false;
  if (document.activeElement === el) return true;
  el.focus({ preventScroll: true });
  return document.activeElement === el;
}

/**
 * Best-effort attempt to pull keyboard focus into the pane, retried a few
 * times while the pane finishes appearing. Whether it works at all is the
 * host's decision, not ours: if PowerPoint keeps focus on the slide the
 * following letters go to the slide, and the user has to click the pane once.
 */
export function focusCodeModeCatcher(): void {
  try {
    window.focus();
  } catch {
    /* not ours to command */
  }
  if (attempt()) return;
  for (const delay of RETRY_DELAYS_MS) {
    window.setTimeout(attempt, delay);
  }
}
